import { Injectable } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ListSearch } from '@fry/lib/list';
import { RolesStore } from '@fry/lib/roles';
import { RelationsStore } from '@fry/lib/relations';
import { categoriesToOptions } from '@fry/lib/utils';

@Injectable()
export class UsersSearch implements ListSearch {
  constructor(
    private roles: RolesStore,
    private relations: RelationsStore,
  ) {}

  private fulltextFilter() {
    return of({
      id: 'fullname',
      label: '',
      featured: true,
      widget: {
        type: 'string',
        placeholder: $localize `Full text search`,
      }
    });
  }

  private rolesFilter() {
    return this.roles.all().pipe(
      map(roles => {
        return {
          id: 'roles',
          label: $localize `Show only users with these roles`,
          widget: {
            type: 'multiSelect'
          },
          options: {
            categories: roles.map(role => {
              return { id: role.doc._id, name: role.doc.title };
            })
          }
        };
      })
    );
  }

  private userStateFilter() {
    return of({
        id: 'state',
        label: $localize `Show only users in these states`,
        widget: {
          type: 'multiSelect'
        },
        options: {
          categories: [
            { id: 'active', name: $localize `Active` },
            { id: 'disabled', name: $localize `Disabled` },
            { id: 'waiting_for_approval', name: $localize `Pending` },
          ]
        }
      });
  }

  private userSourceFilter() {
    return of({
        id: 'source',
        label: $localize `Show only users created via these sources`,
        widget: {
          type: 'multiSelect'
        },
        options: {
          categories: [
            { id: 'booking', name: $localize `risr/apply` },
            { id: 'kaizen', name: $localize `risr/advance` },
            { id: 'self_register', name: $localize `Self register` },
            { id: 'auto_register', name: $localize `SSO auto register` },
            { id: 'import', name: $localize `Bulk import` },
            { id: 'api', name: $localize `API` },
            { id: 'quickfill', name: $localize `Quick fill registration` },
            { id: 'not_classified', name: $localize `Not classified` },
          ]
        }
      });
  }

  private relationFilters() {
    return this.relations.all().pipe(
      map(relations => {
        const fields = relations.map(relation => {
          return {
            id: 'relation$' + relation.doc._id,
            label: relation.doc.name,
            widget: {
              type: 'multiSelect'
            },
            options: {
              categories: categoriesToOptions(relation.doc.categories),
            }
          };
        });
        return fields;
      })
    );
  }

  public filters() {
    const _filters = [
      this.fulltextFilter(),
      this.userStateFilter(),
      this.rolesFilter(),
      this.relationFilters(),
      this.userSourceFilter()
    ];

    // const _filters = this.availableFilters.map(item => {
    //   const fl = new item();
    //   return fl.filter();
    // });

    return forkJoin(_filters).pipe(
      map(result => {
        let filters = [];
        result.forEach(itm => {
          if (Array.isArray(itm)) {
            filters = filters.concat(itm);
          } else {
            filters.push(itm);
          }
        });
        return filters;
      })
    );
  }

  public sorts() {
    return of([
      {
        id: 'firstname',
        name: $localize `First name`
      },
      {
        id: 'lastname',
        name: $localize `Last name`
      },
      {
        id: 'state',
        name: $localize `State`
      },
      {
        id: 'date',
        name: $localize `Date added`
      }
    ]);
  }
}
